import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import { brandLink } from "../../components/PublicLayout";
import SEO from "../../components/SEO";

export default function Products() {
  const { mode, brand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(searchParams.get("category") || "");
  const [search, setSearch] = useState("");
  const [brandsMap, setBrandsMap] = useState({});

  useEffect(() => {
    // Keep URL in sync with category filter
    if (selectedCat) {
      searchParams.set("category", selectedCat);
    } else {
      searchParams.delete("category");
    }
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat]);

  useEffect(() => {
    const loadAux = async () => {
      let catParams = "";
      if (mode === "brand" && brand) {
        catParams = `?brand_id=${brand.id}`;
      }
      const [cats, brs] = await Promise.all([
        api.get(`/categories${catParams}`).then((r) => r.data),
        api.get(`/brands`).then((r) => r.data),
      ]);
      setCategories(cats);
      const m = {};
      brs.forEach((b) => (m[b.id] = b));
      setBrandsMap(m);
    };
    loadAux();
  }, [mode, brand]);

  useEffect(() => {
    const loadProducts = async () => {
      const params = new URLSearchParams();
      if (mode === "brand" && brand) params.set("brand_slug", brand.slug);
      if (selectedCat) params.set("category_id", selectedCat);
      if (search) params.set("search", search);
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data);
    };
    loadProducts();
  }, [mode, brand, selectedCat, search]);

  const selectedCatName = categories.find((c) => c.id === selectedCat)?.name || "";

  return (
    <div className="fade-in-up" data-testid="products-page">
      <SEO title={selectedCatName || "Ürünler"} />
      <div className="border-b border-white/10 py-16 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Katalog</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">Ürünler</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Ürün ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
              data-testid="products-search-input"
            />
          </div>
          <select
            value={selectedCat}
            onChange={(e) => setSelectedCat(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 min-w-[200px]"
            data-testid="products-category-filter"
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-20 text-zinc-500" data-testid="products-empty">
            Henüz ürün bulunmuyor.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
            {products.map((p) => (
              <Link
                key={p.id}
                to={brandLink(`/urunler/${p.slug}`)}
                className="group block border border-white/10 bg-[#0A0A0A] hover:-translate-y-1 transition-all duration-300"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="aspect-square overflow-hidden bg-[#141414] p-8">
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs uppercase tracking-[0.2em]">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-white/10">
                  {mode === "main" && brandsMap[p.brand_id] && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                      {brandsMap[p.brand_id].name}
                    </div>
                  )}
                  <div className="text-sm font-medium line-clamp-1">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-1 uppercase tracking-[0.15em]">
                    {p.stock_status === "in_stock" && "Stokta"}
                    {p.stock_status === "out_of_stock" && "Tükendi"}
                    {p.stock_status === "coming_soon" && "Çok Yakında"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
